import { z } from "zod";

/** Transfers (the files round, 2026-10-30; brief docs/briefs/files-round.md rules 1 and 2, "After the red team's review"): a file over
 *  10 MB in a ticket's reply composer goes to the customer as a download link instead of inside the mail. The browser uploads it straight
 *  to our storage in parts (Amazon's multipart upload; the memory store's own route in tests and on the local stacks); the link and the file
 *  are removed ten days after the reply left. Pure: the numbers, the program rule, the shapes. */

/** the link and the file live this many days from the moment the reply left (the sender's send or the site's hand-off) */
export const TRANSFER_DAYS = 10;
/** a transfer whose reply never left (failed, held for a month) is removed this many days after its upload (finding 6's backstop) */
export const TRANSFER_UNSENT_BACKSTOP_DAYS = 30;
/** one part the browser uploads on its own; Amazon's own ceiling is 10,000 parts per upload */
export const TRANSFER_PART_BYTES = 8 * 1024 * 1024;
export const TRANSFER_MAX_PARTS = 10_000;
/** a part address and a download address stay valid this long (seconds) */
export const TRANSFER_PART_SECONDS = 2 * 3600;
export const TRANSFER_DOWNLOAD_SECONDS = 5 * 60;
/** the first bytes read at the finish to decide the type (the `file-type` library's own sample size) */
export const TRANSFER_SNIFF_BYTES = 4_100;
/** a reply names its 20 ordinary files and up to 10 transfers on top (finding 26); only ordinary files count against the 20 */
export const ATTACHMENT_IDS_MAX = 30;

const MIB = 1024 * 1024;
/** 8 MiB parts, or the smallest whole number of MiB that keeps the file under 10,000 parts (a 1 TB override's backup still fits) */
export function transferPartSize(size: number): number {
  if (size <= TRANSFER_PART_BYTES * TRANSFER_MAX_PARTS) return TRANSFER_PART_BYTES;
  return Math.ceil(size / TRANSFER_MAX_PARTS / MIB) * MIB;
}

/** program files never go out as a link from us: the inbound mail's list plus Java archives, Mac disk images and installers, shell scripts */
export const PROGRAM_EXTENSIONS = ["exe", "scr", "js", "bat", "msi", "cmd", "com", "vbs", "ps1", "jar", "dmg", "pkg", "app", "sh"] as const;
export const PROGRAM_FILES_REFUSAL = "Program files cannot be sent";
/** a program by its last extension, any case ("README" and "exe" have none) */
export function isProgramName(name: string): boolean {
  const dot = name.lastIndexOf(".");
  if (dot <= 0) return false;
  return (PROGRAM_EXTENSIONS as readonly string[]).includes(name.slice(dot + 1).toLowerCase());
}

export const TRANSFER_SMALL_REFUSAL = "A file of 10 MB or less is sent as an attachment";
export const TRANSFER_PUBLIC_REFUSAL = "This reply is posted publicly; files over 10 MB go in a private channel";
/** a connected site's note (the public API) meets this one; the team's own words go into the discussion since 2026-11-02 */
export const TRANSFER_NOTE_REFUSAL = "A note cannot carry a transfer; files over 10 MB go in a reply";
/** ticket discussion round two: the ticket page's Discussion composer and the chat door refuse a transfer on a discussion line */
export const TRANSFER_DISCUSSION_REFUSAL = "A discussion line cannot carry a transfer; files over 10 MB go in a reply";
export const TRANSFER_FAILED = "The upload failed; try again";

/** `POST /api/v1/w/:slug/transfers`: the file's name and declared size; `ticket`, the reply's ticket number, so a public topic refuses at once */
export const TransferStartSchema = z.object({ name: z.string().trim().min(1).max(255), size: z.number().int().positive(), mime: z.string().max(200).optional(), ticket: z.number().int().positive().optional() }).strict();
export type TransferStart = z.infer<typeof TransferStartSchema>;
export const TransferPartsSchema = z.object({ partNumbers: z.array(z.number().int().min(1).max(TRANSFER_MAX_PARTS)).min(1).max(TRANSFER_MAX_PARTS) }).strict();
export const TransferCompleteSchema = z.object({ parts: z.array(z.object({ partNumber: z.number().int().min(1).max(TRANSFER_MAX_PARTS), etag: z.string().min(1).max(200) }).strict()).min(1).max(TRANSFER_MAX_PARTS) }).strict();
export type TransferPartAddress = { partNumber: number; url: string };
export type TransferStarted = { id: string; uploadId: string; partSize: number; parts: TransferPartAddress[] };

/** a transfer as the team's read shows it under the message: until when, how often the customer downloaded it, when it was removed */
export const TransferReadSchema = z.object({ expiresAt: z.string().nullable(), downloads: z.number().int(), lastDownloadAt: z.string().nullable(), removedAt: z.string().nullable() });
export type TransferRead = z.infer<typeof TransferReadSchema>;

const GIB = 1024 ** 3;
/** storage as a person reads it (finding 33): GB, one decimal under 10 GB, none from 10 GB on, never GiB or bytes ("0.8 GB", "1 GB", "250 GB") */
export function formatStorage(bytes: number): string {
  const gb = bytes / GIB;
  const rounded = Math.round(gb * 10) / 10;
  return rounded >= 10 ? `${Math.round(gb)} GB` : `${String(rounded)} GB`;
}

const MB_ = 1024 * 1024;
/** a file's size as a person reads it, everywhere (the composer, the chips, the mail's download line, the download page): KB whole under
 *  1 MB, MB with one decimal under 10 and none from 10 on ("9.5 MB", "20 MB", "300 MB"), GB from half a gigabyte on (`formatStorage`) */
export function formatFileSize(bytes: number): string {
  if (bytes < MB_) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  if (bytes < GIB / 2) { const mb = bytes / MB_; return mb < 9.95 ? `${String(Math.round(mb * 10) / 10)} MB` : `${Math.round(mb)} MB`; }
  return formatStorage(bytes);
}
